import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import 'react-loading-skeleton/dist/skeleton.css';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useNavigate, Link } from 'react-router-dom';
import ImageCarousel from './child/ImageCarousel';
import PlantationInfoModal from './child/PlantationInfoModal';
import PlantationRecommendationCard from './child/PlantationRecommendationCard';

import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom icon for plantation marker
const createCustomIcon = (color = 'green') => {
  return new L.Icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });
};



/** -------------------------
 * Image Modal Component
 * ------------------------*/
const ImageModal = ({ isOpen, onClose, images, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  // Stable handlers using useCallback
  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // Keyboard navigation with stable dependencies
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, handleNext, handlePrev]); // images.length is stable

  if (!isOpen) return null;

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="modal fade show d-block" 
      tabIndex="-1" 
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
      onClick={handleBackgroundClick}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content border-0 bg-transparent">
          <div className="modal-header border-0 bg-transparent">
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body text-center">
            <div className="position-relative">
              <img
                src={images[currentIndex]}
                alt={`Follow-up ${currentIndex + 1} of ${images.length}`} // Fixed: no "image"
                className="img-fluid rounded"
                style={{ maxHeight: '70vh', objectFit: 'contain' }}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Found';
                }}
              />
              
              {images.length > 1 && (
                <>
                  <button
                    className="position-absolute top-50 start-0 translate-middle-y btn btn-dark btn-sm rounded-circle"
                    onClick={handlePrev}
                    style={{ left: '10px' }}
                  >
                    <Icon icon="mdi:chevron-left" />
                  </button>
                  <button
                    className="position-absolute top-50 end-0 translate-middle-y btn btn-dark btn-sm rounded-circle"
                    onClick={handleNext}
                    style={{ right: '10px' }}
                  >
                    <Icon icon="mdi:chevron-right" />
                  </button>
                </>
              )}
            </div>
            
            {images.length > 1 && (
              <div className="mt-3 text-white">
                <small>
                  {currentIndex + 1} of {images.length}
                </small>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/** -------------------------
 * Location Map Component
 * ------------------------*/
const PlantationLocationMap = ({ location }) => {
  if (!location || !location.coordinates || location.coordinates.length < 2) {
    return (
      <div className="card p-3 text-center text-muted">
        <Icon icon="mdi:map-marker-off" width="30" height="30" />
        <p className="mb-0 mt-2 small">Location not available</p>
      </div>
    );
  }

  const [longitude, latitude] = location.coordinates;
  const position = [latitude, longitude];

  return (
    <div className="card p-3">
      <h6 className="card-title mb-3">
        <Icon icon="mdi:map-marker" className="me-2" />
        Locate the Plantation
      </h6>
      <div className="location-info">
        <p className="mb-1 small">
          <strong>Coordinates:</strong> {latitude.toFixed(6)}, {longitude.toFixed(6)}
        </p>
        <p className="mb-2 small text-muted">Type: {location.type}</p>

        <div style={{ height: '300px', borderRadius: '8px', overflow: 'hidden' }}>
          <MapContainer
            center={position}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://www.esri.com/">Esri</a>'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              opacity={0.5}
            />
            <Marker position={position} icon={createCustomIcon('green')}>
              <Popup>
                <div className="text-center">
                  <strong>Plantation Location</strong><br />
                  Latitude: {latitude.toFixed(6)}<br />
                  Longitude: {longitude.toFixed(6)}
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        </div>
        
        <div className="mt-2 small text-muted text-center">
          <Icon icon="mdi:information-outline" className="me-1" />
          Scroll to zoom, drag to pan
        </div>
      </div>
    </div>
  );
};

/** -------------------------
 * QR Code Component
 * ------------------------*/
const QRCodeDisplay = ({ url, data }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div className="position-relative d-inline-block">
      <div 
        className="qr-code-container"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {url ? (
          <img src={url} alt="QR Code" className="qr-code-image" style={{ width: '150px', height: '150px' }} />
        ) : (
          <div className="qr-code-placeholder bg-light d-flex align-items-center justify-content-center" 
               style={{ width: '60px', height: '60px', borderRadius: '4px' }}>
            <Icon icon="mdi:qrcode" width="30" height="30" />
          </div>
        )}
      </div>
      
      {showTooltip && (
        <div className="qr-tooltip position-absolute bg-dark text-white p-2 rounded" 
             style={{ top: '100%', right: '0', zIndex: 1000, marginTop: '5px', minWidth: '200px' }}>
          <p className="mb-1 small">Scan to view this plantation</p>
          {data && (
            <p className="mb-0 small text-muted">
              ID: {data._id || data.id}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/** -------------------------
 * Plantation Staging Flow Component
 * ------------------------*/
const PlantationStagingFlow = ({ plantationData }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  if (!plantationData) return null;

  const { followUps, plantationDate, updatedAt } = plantationData;

  const openImageModal = (images, index = 0) => {
    setSelectedImages(images);
    setSelectedImageIndex(index);
    setModalOpen(true);
  };

  const closeImageModal = () => {
    setModalOpen(false);
    setSelectedImages([]);
    setSelectedImageIndex(0);
  };

  const getOrdinalSuffix = (i) => {
    const j = i % 10;
    const k = i % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  const stages = [
    {
      id: 'planting',
      title: 'Planting',
      date: plantationDate,
      description: 'Initial planting of the saplings',
      icon: 'mdi:sprout',
      color: 'success'
    },
    ...(followUps?.map((followUp, index) => ({
      id: `followup-${index}`,
      title: `${index + 1}${getOrdinalSuffix(index + 1)} Follow-up`,
      date: followUp.updatedAt,
      description: followUp.remarks || 'Follow-up inspection',
      icon: 'mdi:clipboard-check',
      color: 'primary',
      data: followUp
    })) || []),
    {
      id: 'current',
      title: 'Current Status',
      date: updatedAt,
      description: 'Latest update',
      icon: 'mdi:progress-clock',
      color: 'info',
      isCurrent: true
    }
  ].sort((a, b) => new Date(a.date) - new Date(b.date));

  if (stages.length === 0) {
    return (
      <div className="card p-4 text-center text-muted">
        <Icon icon="mdi:timeline-question" width="30" height="30" />
        <p className="mb-0 mt-2">No staging data available</p>
      </div>
    );
  }

  return (
    <>
      <div className="card mt-10">
        <div className="card-header">
          <h5 className="card-title mb-0">
            <Icon icon="mdi:timeline" className="me-2" />
            Plant Growth Timeline
          </h5>
        </div>
        <div className="card-body">
          <div className="timeline">
            {stages.map((stage, index) => (
              <div key={stage.id} className="timeline-item">
                <div className="timeline-marker">
                  <div className={`timeline-icon bg-${stage.color}`}>
                    <Icon icon={stage.icon} />
                  </div>
                  {index < stages.length - 1 && <div className="timeline-line"></div>}
                </div>
                <div className="timeline-content">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">{stage.title}</h6>
                    <span className="badge bg-light text-dark">
                      {formatDate(stage.date)}
                    </span>
                  </div>
                  <p className="mb-2 text-muted">{stage.description}</p>
                  
                  {stage.data && (
                    <div className="stage-details mt-3 p-3 bg-light rounded">
                      <div className="row">
                        <div className="col-md-4">
                          <p className="mb-1 d-flex align-items-center">
                            <Icon icon="mdi:ruler" className="text-success me-1" style={{ fontSize: '16px' }} />
                            <strong className="ms-0">Avg. Height:</strong>
                            <span className="ms-1">{stage.data.averageHeight || 'N/A'} cm</span>
                          </p>
                        </div>
                        <div className="col-md-4">
                          <p className="mb-1 d-flex align-items-center">
                            <Icon icon="mdi:heart" className="text-danger me-1" style={{ fontSize: '16px' }} />
                            <strong className="ms-0">Survival Rate:</strong>
                            <span className="ms-1">{stage.data.survivalRate || 'N/A'}%</span>
                          </p>
                        </div>
                        <div className="col-md-4">
                          <p className="mb-1 d-flex align-items-center">
                            <Icon icon="mdi:calendar" className="text-primary me-1" style={{ fontSize: '16px' }} />
                            <strong className="ms-0">Last Update:</strong>
                            <span className="ms-1">{formatDate(stage.data.updatedAt)}</span>
                          </p>
                        </div>
                      </div>

                      {stage.data.images && stage.data.images.length > 0 && (
                        <div className="mt-3">
                          <h6 className="mb-2 d-flex align-items-center">
                            <Icon icon="mdi:image" className="me-1" style={{ fontSize: '16px' }} />
                            <span>Follow-up Images</span>
                          </h6>
                          <div className="d-flex gap-2">
                            {stage.data.images.slice(0, 3).map((img, imgIndex) => (
                              <div
                                key={imgIndex}
                                className="position-relative cursor-pointer"
                                onClick={() => openImageModal(stage.data.images, imgIndex)}
                                style={{ cursor: 'pointer' }}
                              >
                                <img
                                  src={img}
                                  alt={`Follow-up ${index + 1}, ${imgIndex + 1} of ${stage.data.images.length}`} // Fixed
                                  className="img-thumbnail"
                                  style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/80x80?text=Image+Error';
                                  }}
                                />
                                <div className="position-absolute top-0 end-0 m-1">
                                  <Icon
                                    icon="mdi:magnify-plus-outline"
                                    className="text-white bg-dark bg-opacity-50 rounded-circle p-1"
                                    style={{ fontSize: '12px' }}
                                  />
                                </div>
                              </div>
                            ))}
                            {stage.data.images.length > 3 && (
                              <div
                                className="position-relative cursor-pointer"
                                onClick={() => openImageModal(stage.data.images, 3)}
                                style={{ cursor: 'pointer' }}
                              >
                                <img
                                  src={stage.data.images[3]}
                                  alt={`Follow-up ${index + 1}, additional images`} // Fixed
                                  className="img-thumbnail"
                                  style={{ width: '80px', height: '80px', objectFit: 'cover', opacity: 0.6 }}
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/80x80?text=Image+Error';
                                  }}
                                />
                                <div className="position-absolute top-50 start-50 translate-middle">
                                  <span className="badge bg-dark">+{stage.data.images.length - 3}</span>
                                </div>
                                <div className="position-absolute top-0 end-0 m-1">
                                  <Icon
                                    icon="mdi:magnify-plus-outline"
                                    className="text-white bg-dark bg-opacity-50 rounded-circle p-1"
                                    style={{ fontSize: '12px' }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                          <small className="text-muted mt-2 d-block">
                            Click on any image to view larger
                          </small>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {stage.isCurrent && (
                    <div className="current-status mt-3 p-3 bg-info bg-opacity-10 rounded">
                      <h6 className="mb-2">
                        <Icon icon="mdi:progress-clock" className="me-2" />
                        Current Status
                      </h6>
                      <p className="mb-0">This plantation is currently being monitored. Check back later for updates.</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ImageModal
        isOpen={modalOpen}
        onClose={closeImageModal}
        images={selectedImages}
        initialIndex={selectedImageIndex}
      />
    </>
  );
};

/** -------------------------
 * Main Component
 * ------------------------*/
const PlantationViewLayer = ({ id }) => {
  const [plantationData, setPlantationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [showPlantingInfo, setShowPlantingInfo] = useState(false);
  const [recommendationData, setRecommendationData] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const APP_BASE_URL = process.env.REACT_APP_URL;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecommendationData = async () => {
      try {
        if (!id) return;

        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await axios.post(
          `${API_BASE_URL}/api/virtualAssis/pafs/get-recommendation-by-plantation-id`,
          { id },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.status && response.data.recommendations && response.data.recommendations.length > 0) {
          setRecommendationData(response.data.recommendations[0]);
        }
      } catch (err) {
        console.error('Error fetching recommendation data:', err);
      }
    };

    fetchRecommendationData();
  }, [id, API_BASE_URL]);

  const [weatherNotification, setWeatherNotification] = useState("");

  useEffect(() => {
    const fetchWeatherNotification = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await axios.get(
          `${API_BASE_URL}/api/virtualAssis/pafs/get-weather-recommendations`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (response.data.success && response.data.notifications && response.data.notifications.length > 0) {
          const body = response.data.notifications[0].body;
          setWeatherNotification(body?.value || body || "");
        }
      } catch (err) {
        console.error('Error fetching weather notification:', err);
      }
    };

    fetchWeatherNotification();
  }, [API_BASE_URL]);

  const getStatusClasses = (category) => {
    switch (category) {
      case 'DEPARTMENTAL':
        return 'bg-success text-white';
      case 'PRIVATE':
        return 'bg-primary text-white';
      case 'NGO':
        return 'bg-info text-white';
      default:
        return 'bg-secondary text-white';
    }
  };

  const renderSpeciesName = (speciesArray) => {
    return speciesArray && speciesArray.length ? speciesArray.join(', ') : 'No species data';
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    const generateQRCode = async () => {
      if (plantationData) {
        try {
          const qrCodeData = `${APP_BASE_URL}/plantations/view/${plantationData._id || plantationData.id}`;
          const url = await QRCode.toDataURL(qrCodeData, { width: 100 });
          setQrCodeUrl(url);
        } catch (err) {
          console.error('Error generating QR code:', err);
        }
      }
    };

    generateQRCode();
  }, [plantationData, APP_BASE_URL]);

  useEffect(() => {
    const fetchPlantationData = async () => {
      try {
        if (!id) throw new Error('No plantation ID provided');

        const token = localStorage.getItem('token');
        if (!token) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }

        const response = await axios.post(
          `${API_BASE_URL}/api/plantation/pafs/get-plantation-by-id`,
          { id },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.status) {
          setPlantationData(response.data.data);
          setTimeout(() => {
            setShowPlantingInfo(true);
          }, 1500);
        } else {
          if (response.data.message === 'Unauthorized') {
            setAccessDenied(true);
          } else {
            throw new Error(response.data.message || 'Invalid response status');
          }
        }
      } catch (error) {
        if (error.response?.status === 401) {
          setAccessDenied(true);
        } else {
          setError(error.message);
          toast.error('Failed to fetch plantation data.');
          console.error('Error fetching plantation data:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPlantationData();
  }, [API_BASE_URL, id]);

  if (accessDenied) {
    navigate('/access-denied');
    return null;
  }

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner-border text-primary" role="status"></div>
        <h3 className="mt-3">Loading Plantation Details...</h3>
      </div>
    );
  }

  if (error || !plantationData) {
    const message = error
      ? 'Error Loading Plantation Details'
      : 'No Plantation Data Found';
    const color = error ? '#dc3545' : '#fd7e14';

    return (
      <div className="loading-overlay">
        <Icon icon="material-symbols:error-outline" width="50" height="50" color={color} />
        <h3 className="mt-3" style={{ color }}>{message}</h3>
        <p className="text-center mt-2" style={{ maxWidth: '500px' }}>
          {error || 'The plantation details could not be found.'}
        </p>
        <Link to="/dashboard" className="btn btn-success mt-3 d-flex align-items-center gap-2">
          <Icon icon="ri:home-4-line" /> Go Back To Home
        </Link>
      </div>
    );
  }

  return (
    <div className="container my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Link to="/dashboard" className="btn btn-outline-secondary d-flex align-items-center gap-2">
          <Icon icon="ri:arrow-left-line" /> Back
        </Link>
        <div className="text-center">
          <h2 className="mb-0">Plantation Details</h2>
          <span className={`px-3 py-1 rounded-pill fw-semibold ${getStatusClasses(plantationData.category)}`}>
            {plantationData.category || 'No Data Found'}
          </span>
        </div>
        <div className="d-flex flex-column align-items-center">
          <QRCodeDisplay url={qrCodeUrl} data={plantationData} />
          <div className="mt-2">
            {plantationData.plantationKey || 'No Data Found'}
          </div>
        </div>
      </div>

      <hr />

      <div className="row">
        <div className="col-md-6">
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <Icon icon="mdi:information-outline" className="me-2" />
                Plantation Information
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                {[
                  { icon: 'mdi:map', text: 'Province', value: plantationData.province, color: 'text-primary' },
                  { icon: 'mdi:map-outline', text: 'District', value: plantationData.district, color: 'text-info' },
                  { icon: 'mdi:map-marker', text: 'Tehsil', value: plantationData.tehsil, color: 'text-warning' },
                  { icon: 'mdi:nature', text: 'Site Type', value: plantationData.siteType, color: 'text-info' },
                  { icon: 'mdi:tree', text: 'Number of Plants', value: plantationData.numberOfPlants, color: 'text-success' },
                  { icon: 'mdi:leaf', text: 'Species', value: renderSpeciesName(plantationData.speciesName), color: 'text-success' },
                  ...(plantationData.category === "DEPARTMENTAL" ? [
                    { icon: 'mdi:home', text: 'Site Name', value: plantationData.siteName, color: 'text-primary' },
                    { icon: 'mdi:flag', text: 'Program', value: plantationData.plantationProgram, color: 'text-warning' },
                    { icon: 'mdi:sprout', text: 'Plantation Type', value: plantationData.plantationType, color: 'text-success' },
                    { icon: 'mdi:office-building', text: 'Department', value: plantationData.departmentName, color: 'text-success' },
                  ] : []),
                  { icon: 'mdi:megaphone', text: 'Plantation Campaign', value: plantationData.plantationCampaign, color: 'text-success' },
                  { icon: 'mdi:leaf', text: 'Plant Type', value: plantationData.plantType, color: 'text-success' },
                  { icon: 'mdi:account', text: 'Planted By', value: plantationData.plantedBy, color: 'text-secondary' },
                  { icon: 'mdi:calendar', text: 'Plantation Date', value: formatDate(plantationData.plantationDate), color: 'text-danger' },
                ].map((item, index) => {
                  const originalValue = item.value || 'No Data Found';
                  const strValue = String(originalValue);
                  const isTruncated = strValue.length > 13;
                  const displayValue = isTruncated ? strValue.substring(0, 13) + '...' : strValue;

                  return (
                    <div key={index} className="col-md-6 mb-3">
                      <p className="mb-1 d-flex align-items-center">
                        <Icon icon={item.icon} className={`me-2 ${item.color}`} />
                        <strong>{item.text}:</strong>
                        <span 
                          className="ms-1" 
                          title={isTruncated ? strValue : ''} 
                          style={{ cursor: isTruncated ? 'help' : 'default' }}
                        >
                          {displayValue}
                        </span>
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <PlantationLocationMap location={plantationData.location} />
        </div>

        <div className="col-md-6">
          <ImageCarousel images={plantationData.images || []} />
        </div>
      </div>

      <PlantationRecommendationCard recommendation={recommendationData} />
      <PlantationStagingFlow plantationData={plantationData} />
      
      <PlantationInfoModal 
        isOpen={showPlantingInfo}
        onClose={() => setShowPlantingInfo(false)}
        message={`${weatherNotification}`}
      />
      
      <style>
        {`
          .timeline {
            position: relative;
            padding-left: 2rem;
          }
          
          .timeline-item {
            position: relative;
            margin-bottom: 2rem;
          }
          
          .timeline-marker {
            position: absolute;
            left: -2rem;
            top: 0;
            z-index: 2;
          }
          
          .timeline-icon {
            width: 2.5rem;
            height: 2.5rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
          }
          
          .timeline-line {
            position: absolute;
            left: 50%;
            top: 2.5rem;
            bottom: -2rem;
            width: 2px;
            background-color: #dee2e6;
            transform: translateX(-50%);
          }
          
          .timeline-content {
            margin-left: 1rem;
            padding: 1rem;
            border-radius: 0.5rem;
            background-color: #f8f9fa;
            border-left: 3px solid #0d6efd;
          }
          
          .timeline-item:last-child .timeline-line {
            display: none;
          }
          
          .cursor-pointer {
            cursor: pointer;
          }
          
          .cursor-pointer:hover {
            opacity: 0.8;
          }
        `}
      </style>
    </div>
  );
};
export default PlantationViewLayer;